import React from 'react'
import { mergeClassnames } from '@mote/helpers/dom'
import { x } from '@xstyled/styled-components'
import type { HTMLXStyledProps } from '../../system'
import { forwardRef } from '../../system'
import {
  SliderRoot,
  SliderThumbRoot,
  SliderTrackFilledRoot,
  SliderTrackRoot
} from './Slider.styles'
import {
  SliderContextProvider,
  useSliderContext
} from './SliderContextProvider'
import type { UseSliderProps } from './useSlider'
import { useSlider } from './useSlider'

export interface SliderProps
  extends UseSliderProps,
    Omit<HTMLXStyledProps<'div'>, keyof UseSliderProps> {}

/**
 * Modified Chakra UI Slider component.
 * It provides context and functionality for all slider components
 *
 * @see Docs     https://chakra-ui.com/docs/form/slider
 * @see Source   https://github.com/chakra-ui/chakra-ui/blob/main/packages/slider/src/slider.tsx
 * @see WAI-ARIA https://www.w3.org/TR/wai-aria-practices/#slider
 */
export const Slider = forwardRef<SliderProps, 'div'>((props, ref) => {
  const { getInputProps, getRootProps, ...context } = useSlider(props)

  const rootProps = getRootProps()
  const inputProps = getInputProps({}, ref)

  return (
    <SliderContextProvider value={context}>
      <SliderRoot
        {...rootProps}
        className={mergeClassnames('range-slider', props.className)}
      >
        {props.children}
        <input {...inputProps} />
      </SliderRoot>
    </SliderContextProvider>
  )
})

export interface SliderThumbProps extends HTMLXStyledProps<'div'> {}

export const SliderThumb = forwardRef<SliderThumbProps, 'div'>((props, ref) => {
  const sliderContext = useSliderContext()
  const thumbProps = sliderContext.getThumbProps(props, ref)

  return (
    <SliderThumbRoot
      {...thumbProps}
      className={mergeClassnames('range-slider__thumb', props.className)}
    />
  )
})

export interface SliderTrackProps extends HTMLXStyledProps<'div'> {}

export const SliderTrack = forwardRef<SliderTrackProps, 'div'>((props, ref) => {
  const sliderContext = useSliderContext()
  const trackProps = sliderContext.getTrackProps(props, ref)

  return (
    <SliderTrackRoot
      {...trackProps}
      className={mergeClassnames('range-slider__track', props.className)}
    />
  )
})

export interface SliderFilledTrackProps extends HTMLXStyledProps<'div'> {}

export const SliderFilledTrack = forwardRef<SliderFilledTrackProps, 'div'>(
  (props, ref) => {
    const sliderContext = useSliderContext()
    const trackProps = sliderContext.getInnerTrackProps(props, ref)

    return (
      <SliderTrackFilledRoot
        {...trackProps}
        className={mergeClassnames(
          'range-slider__filled-track',
          props.className
        )}
      />
    )
  }
)

export interface SliderMarkProps extends HTMLXStyledProps<'div'> {
  value: number
}

export const SliderMark = forwardRef<SliderMarkProps, 'div'>((props, ref) => {
  const sliderContext = useSliderContext()
  const markProps = sliderContext.getMarkerProps(props, ref)

  return (
    <x.div
      {...markProps}
      className={mergeClassnames('range-slider__marker', props.className)}
    />
  )
})
